import { useMemo } from 'react';
import styled from 'styled-components';

const Wrapper = styled.div`
button:hover {
    color: inherit;
}
`;

interface Props {
    page: number;
    totalPages: number;
    loading?: boolean;
    changePage: (page: number) => void;
}

function range(start: number, end: number): number[] {
    const length = end - start + 1;

    return Array.from({ length }, (_, index) => index + start);
}

export const Pagination = ({ page, totalPages, changePage, loading = false }: Props): React.ReactElement => {
    const handlePage = (newPage: number) => (): void => {
        if (newPage <= 0 || newPage > totalPages) {
            return;
        }

        changePage(newPage);
    };

    const DOTS = -1 as const,
     boundaries = 1 as const,
      siblings = 1 as const;

    const pages = useMemo((): number[] => {
        const totalPageNumbers = siblings * 2 + 3 + boundaries * 2;

        if (totalPageNumbers >= totalPages) {
          return range(1, totalPages);
        }

        const leftSiblingIndex = Math.max(page - siblings, boundaries);
        const rightSiblingIndex = Math.min(page + siblings, totalPages - boundaries);

        const shouldShowLeftDots = leftSiblingIndex > boundaries + 2;
        const shouldShowRightDots = rightSiblingIndex < totalPages - (boundaries + 1);

        if (!shouldShowLeftDots && shouldShowRightDots) {
          const leftItemCount = siblings * 2 + boundaries + 2;

          return [...range(1, leftItemCount), DOTS, ...range(totalPages - (boundaries - 1), totalPages)];
        }

        if (shouldShowLeftDots && !shouldShowRightDots) {
          const rightItemCount = boundaries + 1 + 2 * siblings;

          return [...range(1, boundaries), DOTS, ...range(totalPages - rightItemCount, totalPages)];
        }

        return [
          ...range(1, boundaries),
          DOTS,
          ...range(leftSiblingIndex, rightSiblingIndex),
          DOTS,
          ...range(totalPages - boundaries + 1, totalPages)
        ];
      }, [totalPages, page]);

    return (
        <Wrapper className="d-flex justify-content-center gap-32px text-white m-12px w-25 mx-auto">
            <button disabled={loading || page <= 1} onClick={handlePage(page - 1)} type="button" className="btn x-pseudo">&lt;</button>
            {pages.map((value, i) => value !== DOTS ? (
                <button key={value} disabled={loading} type="button"
                onClick={handlePage(value)}
                className={`btn x-pseudo ${page === value ? 'text-primary' : ''}`}>{value}</button>
            ) : (
                // eslint-disable-next-line react/no-array-index-key
                <button key={`dots-${value}-${i}`} type="button" disabled
                className='btn x-pseudo'
                >...</button>
            ))}
            <button disabled={loading || page >= totalPages} onClick={handlePage(page + 1)} type="button" className="btn x-pseudo">&gt;</button>
        </Wrapper>
    );
};
